import { Course } from '@/types/courseTypes';

export const nvidiaAIPlatformCourse: Course = {
  id: 'nvidia-ai-platform',
  title: 'NVIDIA AI & GPU Computing',
  description: 'Master GPU-accelerated computing, CUDA programming, and NVIDIA AI frameworks for deep learning and edge deployment',
  level: 'intermediate',
  duration: '20 hours',
  durationHours: 20,
  image: '/placeholder.svg',
  tags: ['NVIDIA', 'GPU Computing', 'CUDA', 'TensorRT', 'Jetson', 'AI Infrastructure'],
  prerequisites: ['Python programming', 'Basic machine learning concepts', 'Linux command line'],
  learningOutcomes: [
    'Write efficient CUDA code for GPU acceleration',
    'Optimize deep learning models with TensorRT',
    'Deploy AI models on NVIDIA Jetson edge devices',
    'Build production inference pipelines with Triton',
    'Leverage NVIDIA NGC catalog for pre-trained models'
  ],
  type: 'specialized-ai',
  modules: [
    {
      id: 'nvidia-module-1',
      title: 'NVIDIA GPU Architecture & CUDA Fundamentals',
      description: 'Learn GPU computing paradigms and CUDA programming basics',
      content: `# NVIDIA GPU Architecture & CUDA Fundamentals

## GPU vs CPU Computing

### Architectural Differences
- **CPUs**: Few powerful cores optimized for sequential processing
- **GPUs**: Thousands of smaller cores designed for parallel workloads
- **Use Cases**: GPUs excel at matrix operations, image processing, and neural networks

### CUDA Programming Model
CUDA (Compute Unified Device Architecture) enables general-purpose GPU programming.

**Key Concepts**:
- **Host**: CPU and its memory
- **Device**: GPU and its memory
- **Kernel**: Function that runs on GPU
- **Thread Hierarchy**: Grid → Blocks → Threads

## Memory Hierarchy

### Types of GPU Memory
1. **Global Memory**: Large but slow, accessible by all threads
2. **Shared Memory**: Fast, per-block memory for thread cooperation
3. **Registers**: Fastest, per-thread private memory
4. **Constant/Texture Memory**: Read-only optimized caches

### Memory Optimization Patterns
- Coalesced memory access for bandwidth efficiency
- Shared memory for data reuse
- Minimizing host-device transfers

## Parallel Computing Patterns

### Common CUDA Patterns
1. **Map**: Apply function to each element independently
2. **Reduce**: Aggregate data (sum, max, min)
3. **Scan**: Prefix sum operations
4. **Stencil**: Compute from local neighbors

### Performance Considerations
- Occupancy: Maximize active threads per SM
- Warp divergence: Avoid conditional branching
- Bank conflicts: Optimize shared memory access`,
      duration: '4 hours',
      quiz: {
        questions: [
          {
            id: 'nv1-q1',
            question: 'What is the primary advantage of GPU computing over CPU computing for AI workloads?',
            options: [
              'Lower power consumption',
              'Massive parallelism with thousands of cores',
              'Faster clock speeds',
              'Larger cache memory'
            ],
            correctAnswer: 1,
            explanation: 'GPUs have thousands of cores optimized for parallel workloads, making them ideal for matrix operations in neural networks.'
          },
          {
            id: 'nv1-q2',
            question: 'In CUDA, what is a kernel?',
            options: [
              'A Linux operating system component',
              'A function that runs on the CPU',
              'A function that runs on the GPU',
              'A memory management unit'
            ],
            correctAnswer: 2,
            explanation: 'A CUDA kernel is a function that executes on the GPU device, launched from host code.'
          },
          {
            id: 'nv1-q3',
            question: 'Which GPU memory type provides the fastest access for thread-local data?',
            options: [
              'Global memory',
              'Shared memory',
              'Registers',
              'Constant memory'
            ],
            correctAnswer: 2,
            explanation: 'Registers are the fastest memory type, providing per-thread private storage with minimal latency.'
          }
        ]
      }
    },
    {
      id: 'nvidia-module-2',
      title: 'NVIDIA AI Frameworks & TensorRT',
      description: 'Optimize deep learning models with TensorRT and CUDA-accelerated libraries',
      content: `# NVIDIA AI Frameworks & TensorRT

## TensorRT Deep Learning Optimizer

### What is TensorRT?
TensorRT is NVIDIA's high-performance deep learning inference optimizer and runtime.

**Key Features**:
- Layer fusion and precision calibration
- Kernel auto-tuning for target GPU
- Dynamic tensor memory management
- Multi-stream execution

### TensorRT Workflow
1. **Import**: Load model from framework (PyTorch, TensorFlow, ONNX)
2. **Optimize**: Build optimized engine with FP32/FP16/INT8
3. **Deploy**: Execute inference with minimal latency

### Precision Optimization
\`\`\`python
import tensorrt as trt

# Create builder and network
builder = trt.Builder(TRT_LOGGER)
network = builder.create_network()

# Configure precision
config = builder.create_builder_config()
config.set_flag(trt.BuilderFlag.FP16)  # Use FP16 precision

# Build optimized engine
engine = builder.build_engine(network, config)
\`\`\`

## CUDA-Accelerated Libraries

### cuDNN (Deep Neural Network Library)
- Optimized primitives for convolution, pooling, normalization
- Automatic algorithm selection
- Multi-GPU support

### cuBLAS (Linear Algebra)
- GPU-accelerated BLAS operations
- Matrix multiplication optimization
- Batched operations

### NCCL (Multi-GPU Communication)
- Efficient collective operations (AllReduce, Broadcast)
- Topology-aware communication
- RDMA support for multi-node

## NVIDIA Triton Inference Server

### Production Deployment
Triton enables scalable model serving for multiple frameworks.

**Features**:
- Dynamic batching for throughput
- Concurrent model execution
- Model versioning and A/B testing
- HTTP/gRPC/C++ APIs

### Triton Configuration
\`\`\`python
# model_config.pbtxt
name: "resnet50"
platform: "tensorrt_plan"
max_batch_size: 8
input {
  name: "input"
  data_type: TYPE_FP32
  dims: [3, 224, 224]
}
output {
  name: "output"
  data_type: TYPE_FP32
  dims: [1000]
}
\`\`\`

## Performance Optimization Strategies

### Memory Optimization
- Use mixed precision (FP16/INT8)
- Minimize CPU-GPU transfers
- Pre-allocate buffers

### Throughput Optimization
- Dynamic batching
- Multi-stream execution
- Asynchronous operations`,
      duration: '5 hours',
      quiz: {
        questions: [
          {
            id: 'nv2-q1',
            question: 'What does TensorRT primarily optimize for deep learning models?',
            options: [
              'Training speed',
              'Model accuracy',
              'Inference performance',
              'Memory footprint during training'
            ],
            correctAnswer: 2,
            explanation: 'TensorRT is specifically designed to optimize inference performance through layer fusion, precision calibration, and kernel optimization.'
          },
          {
            id: 'nv2-q2',
            question: 'Which precision mode in TensorRT provides the best balance of speed and accuracy for most models?',
            options: [
              'FP32 (32-bit floating point)',
              'FP16 (16-bit floating point)',
              'INT8 (8-bit integer)',
              'FP64 (64-bit floating point)'
            ],
            correctAnswer: 1,
            explanation: 'FP16 typically provides 2-3x speedup over FP32 with minimal accuracy loss for most neural networks.'
          }
        ]
      }
    },
    {
      id: 'nvidia-module-3',
      title: 'NVIDIA Jetson for Edge AI',
      description: 'Deploy AI models on edge devices using Jetson platform',
      content: `# NVIDIA Jetson for Edge AI

## Jetson Platform Overview

### Jetson Product Line
- **Jetson Nano**: Entry-level, 5W-10W, $59-99
- **Jetson Xavier NX**: Mid-range, 10W-15W, 21 TOPS
- **Jetson AGX Orin**: High-end, 15W-60W, 275 TOPS
- **Jetson Orin Nano**: Latest compact, 7W-15W, 40 TOPS

### Key Features
- Integrated GPU, CPU, and AI accelerators
- CUDA, cuDNN, TensorRT support
- Power-efficient inference
- Industrial temperature range

## JetPack SDK

### Components
- **Linux for Tegra (L4T)**: Ubuntu-based OS
- **CUDA Toolkit**: GPU programming
- **cuDNN, TensorRT**: AI inference
- **VisionWorks**: Computer vision
- **Multimedia APIs**: Camera, video encode/decode

### Installation & Setup
\`\`\`bash
# Flash Jetson with SDK Manager
# Or use pre-built image

# Verify installation
jetson_release

# Check GPU
nvidia-smi

# Install additional packages
sudo apt install python3-pip
pip3 install jetson-stats
\`\`\`

## Real-Time Inference Optimization

### DeepStream SDK
Hardware-accelerated video analytics framework.

**Pipeline Elements**:
1. Video decode (NVDEC)
2. Pre-processing (VPU)
3. Inference (DLA/GPU)
4. Post-processing
5. Video encode (NVENC)

### DeepStream Python Example
\`\`\`python
import gi
gi.require_version('Gst', '1.0')
from gi.repository import Gst

# Create pipeline
pipeline = Gst.parse_launch("""
    filesrc location=input.mp4 !
    qtdemux ! h264parse ! nvv4l2decoder !
    m.sink_0 nvstreammux name=m batch-size=1 !
    nvinfer config-file-path=config.txt !
    nvvideoconvert ! nvdsosd !
    nvv4l2h264enc ! h264parse !
    qtmux ! filesink location=output.mp4
""")

pipeline.set_state(Gst.State.PLAYING)
\`\`\`

## Computer Vision Applications

### Common Use Cases
- Object detection and tracking
- Pose estimation
- Semantic segmentation
- Optical character recognition
- Anomaly detection

### Jetson Inference Library
\`\`\`python
import jetson.inference
import jetson.utils

# Load detection network
net = jetson.inference.detectNet("ssd-mobilenet-v2")

# Load image
img = jetson.utils.loadImage("image.jpg")

# Detect objects
detections = net.Detect(img)

# Process results
for detection in detections:
    print(f"Class: {net.GetClassDesc(detection.ClassID)}")
    print(f"Confidence: {detection.Confidence}")
\`\`\`

## Power Management

### Power Modes
\`\`\`bash
# List available modes
sudo nvpmodel -q

# Set power mode (Jetson Orin)
sudo nvpmodel -m 0  # MAXN (maximum performance)
sudo nvpmodel -m 1  # 50W mode
sudo nvpmodel -m 2  # 30W mode
sudo nvpmodel -m 3  # 15W mode

# Monitor power consumption
sudo tegrastats
\`\`\`

### Optimization Tips
- Use TensorRT for model optimization
- Enable DLA (Deep Learning Accelerator) when available
- Batch processing when possible
- Profile with Nsight Systems`,
      duration: '5 hours',
      quiz: {
        questions: [
          {
            id: 'nv3-q1',
            question: 'What is the primary advantage of the Jetson platform for edge AI deployment?',
            options: [
              'Lowest cost among all edge devices',
              'Integrated GPU with AI accelerators in a power-efficient package',
              'Largest model capacity',
              'Wireless connectivity'
            ],
            correctAnswer: 1,
            explanation: 'Jetson combines GPU, CPU, and AI accelerators in a single SoM with excellent performance-per-watt.'
          },
          {
            id: 'nv3-q2',
            question: 'What is DeepStream SDK primarily used for on Jetson devices?',
            options: [
              'Training neural networks',
              'Hardware-accelerated video analytics',
              'Web application development',
              'Database management'
            ],
            correctAnswer: 1,
            explanation: 'DeepStream provides a complete framework for hardware-accelerated video analytics pipelines on Jetson.'
          }
        ]
      }
    },
    {
      id: 'nvidia-module-4',
      title: 'NVIDIA NGC & Cloud AI',
      description: 'Leverage NGC catalog and deploy GPU workloads in the cloud',
      content: `# NVIDIA NGC & Cloud AI

## NVIDIA GPU Cloud (NGC)

### NGC Catalog
Pre-trained models, containers, and SDKs for accelerated computing.

**Categories**:
- AI/ML containers (PyTorch, TensorFlow, JAX)
- Pre-trained models (vision, NLP, speech)
- Helm charts for Kubernetes
- Industry-specific solutions

### Using NGC Containers
\`\`\`bash
# Pull PyTorch container
docker pull nvcr.io/nvidia/pytorch:24.01-py3

# Run with GPU support
docker run --gpus all -it --rm \\
  -v /data:/data \\
  nvcr.io/nvidia/pytorch:24.01-py3

# Verify CUDA
python -c "import torch; print(torch.cuda.is_available())"
\`\`\`

### NGC Pre-trained Models
\`\`\`python
import torch
from torchvision import models

# Download from NGC
model = torch.hub.load('NVIDIA/DeepLearningExamples:torchhub',
                       'nvidia_resnet50',
                       pretrained=True)
model.eval().cuda()

# Inference
with torch.no_grad():
    output = model(input_tensor)
\`\`\`

## RAPIDS for GPU Data Science

### RAPIDS Ecosystem
- **cuDF**: GPU DataFrame (pandas-like)
- **cuML**: GPU ML algorithms (scikit-learn-like)
- **cuGraph**: GPU graph analytics
- **cuSpatial**: GPU spatial analytics

### RAPIDS Example
\`\`\`python
import cudf
import cuml
from cuml.ensemble import RandomForestClassifier

# GPU DataFrame
gdf = cudf.read_csv('data.csv')

# GPU-accelerated ML
X = gdf[['feature1', 'feature2']].values
y = gdf['target'].values

model = RandomForestClassifier(n_estimators=100)
model.fit(X, y)

# 10-50x faster than CPU scikit-learn
\`\`\`

## Multi-Cloud GPU Deployment

### AWS EC2 GPU Instances
\`\`\`bash
# P4d instances (A100 GPUs)
# G5 instances (A10G GPUs)
# P3 instances (V100 GPUs)

# Launch with Deep Learning AMI
aws ec2 run-instances \\
  --image-id ami-0xyz \\
  --instance-type p3.2xlarge \\
  --key-name my-key
\`\`\`

### Google Cloud GPU
\`\`\`bash
# Create instance with A100
gcloud compute instances create gpu-instance \\
  --zone=us-central1-a \\
  --machine-type=a2-highgpu-1g \\
  --accelerator=type=nvidia-tesla-a100,count=1 \\
  --image-family=pytorch-latest-gpu \\
  --image-project=deeplearning-platform-release
\`\`\`

### Azure GPU VMs
\`\`\`bash
# NC-series (V100, A100)
az vm create \\
  --resource-group myRG \\
  --name gpuVM \\
  --size Standard_NC6s_v3 \\
  --image nvidia-ngc-base-2204
\`\`\`

## Kubernetes with GPU

### NVIDIA Device Plugin
\`\`\`yaml
# gpu-pod.yaml
apiVersion: v1
kind: Pod
metadata:
  name: gpu-pod
spec:
  containers:
  - name: cuda-container
    image: nvcr.io/nvidia/cuda:12.0-base
    resources:
      limits:
        nvidia.com/gpu: 1
\`\`\`

### Multi-GPU Training with Kubeflow
\`\`\`yaml
apiVersion: kubeflow.org/v1
kind: PyTorchJob
metadata:
  name: pytorch-dist
spec:
  pytorchReplicaSpecs:
    Master:
      replicas: 1
      template:
        spec:
          containers:
          - name: pytorch
            image: nvcr.io/nvidia/pytorch:24.01-py3
            resources:
              limits:
                nvidia.com/gpu: 4
\`\`\`

## Cost Optimization Strategies

### Spot Instances
- Use spot/preemptible for fault-tolerant workloads
- Checkpointing for long training jobs
- Multi-instance for redundancy

### Right-Sizing
- Profile GPU utilization
- Match GPU memory to model size
- Use MIG (Multi-Instance GPU) for A100

### Reserved Capacity
- Commit for sustained workloads
- Significant discounts (30-70%)
- Flexibility with instance families`,
      duration: '6 hours',
      quiz: {
        questions: [
          {
            id: 'nv4-q1',
            question: 'What is the primary purpose of NVIDIA NGC?',
            options: [
              'GPU hardware marketplace',
              'Catalog of optimized containers and pre-trained models',
              'Cloud gaming platform',
              'Graphics rendering service'
            ],
            correctAnswer: 1,
            explanation: 'NGC provides a catalog of GPU-optimized containers, pre-trained models, and SDKs for accelerated computing.'
          },
          {
            id: 'nv4-q2',
            question: 'Which RAPIDS library provides GPU-accelerated DataFrame operations?',
            options: [
              'cuML',
              'cuGraph',
              'cuDF',
              'cuSpatial'
            ],
            correctAnswer: 2,
            explanation: 'cuDF provides pandas-like DataFrame operations accelerated on GPU, often 10-50x faster than pandas.'
          }
        ]
      }
    }
  ]
};
